import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type ConfirmRequest = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
};

export type ConfirmDialogProps = ConfirmRequest & {
  open: boolean;
  theme?: 'dark' | 'light';
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  theme = 'dark',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const overlay = theme === 'dark' ? 'bg-black/60' : 'bg-gray-900/40';
  const panel = theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200';
  const titleClass = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const messageClass = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const cancelClass =
    theme === 'dark'
      ? 'border-gray-500 text-gray-200 hover:bg-gray-700'
      : 'border-gray-300 text-gray-700 hover:bg-gray-100';
  const confirmClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-500 text-white border-red-600'
      : theme === 'dark'
        ? 'bg-cyan-600 hover:bg-cyan-500 text-white border-cyan-600'
        : 'bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-600';

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${overlay}`}
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className={`w-full max-w-md rounded-xl border shadow-2xl ${panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3">
          <h2 id="confirm-dialog-title" className={`text-lg font-semibold ${titleClass}`}>
            {title}
          </h2>
          <p id="confirm-dialog-message" className={`mt-2 text-sm leading-relaxed ${messageClass}`}>
            {message}
          </p>
        </div>
        <div className="flex justify-end gap-3 px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${cancelClass}`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Promise-based confirm — works in Teams where window.confirm is blocked. */
export function useConfirmDialog(theme: 'dark' | 'light' = 'dark') {
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback((req: ConfirmRequest): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setRequest(req);
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setRequest(null);
  }, []);

  const handleCancel = useCallback(() => close(false), [close]);
  const handleConfirm = useCallback(() => close(true), [close]);

  const dialog = (
    <ConfirmDialog
      open={!!request}
      title={request?.title ?? ''}
      message={request?.message ?? ''}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      variant={request?.variant}
      theme={theme}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, dialog };
}

export type NoticeVariant = 'default' | 'warning' | 'error';

export type NoticeRequest = {
  title?: string;
  message: string;
  okLabel?: string;
  variant?: NoticeVariant;
};

export type NoticeDialogProps = NoticeRequest & {
  open: boolean;
  theme?: 'dark' | 'light';
  onClose: () => void;
};

function NoticeIcon({ variant, theme }: { variant: NoticeVariant; theme: 'dark' | 'light' }) {
  if (variant === 'default') return null;
  const iconWrap =
    variant === 'warning'
      ? theme === 'dark'
        ? 'bg-amber-500/15 text-amber-300'
        : 'bg-amber-100 text-amber-700'
      : theme === 'dark'
        ? 'bg-red-500/15 text-red-300'
        : 'bg-red-100 text-red-700';
  return (
    <span
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
      aria-hidden="true"
    >
      {variant === 'warning' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      )}
    </span>
  );
}

/** Single-button notice — replacement for window.alert in Teams. */
export function NoticeDialog({
  open,
  title = 'Notice',
  message,
  okLabel = 'OK',
  variant = 'default',
  theme = 'dark',
  onClose,
}: NoticeDialogProps) {
  if (!open) return null;

  const overlay = theme === 'dark' ? 'bg-black/60' : 'bg-gray-900/40';
  const panel = theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200';
  const titleClass =
    variant === 'warning'
      ? theme === 'dark'
        ? 'text-amber-300'
        : 'text-amber-700'
      : variant === 'error'
        ? theme === 'dark'
          ? 'text-red-300'
          : 'text-red-700'
        : theme === 'dark'
          ? 'text-white'
          : 'text-gray-900';
  const messageClass = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const okClass =
    variant === 'warning'
      ? theme === 'dark'
        ? 'bg-amber-600 hover:bg-amber-500 text-white'
        : 'bg-amber-600 hover:bg-amber-700 text-white'
      : variant === 'error'
        ? 'bg-red-600 hover:bg-red-500 text-white'
        : theme === 'dark'
          ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
          : 'bg-cyan-600 hover:bg-cyan-700 text-white';

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${overlay}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="notice-dialog-title"
        aria-describedby="notice-dialog-message"
        className={`w-full max-w-md rounded-xl border shadow-2xl ${panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start gap-3">
            <NoticeIcon variant={variant} theme={theme} />
            <div className="min-w-0 flex-1">
              <h2 id="notice-dialog-title" className={`text-lg font-semibold ${titleClass}`}>
                {title}
              </h2>
              <p id="notice-dialog-message" className={`mt-2 text-sm leading-relaxed ${messageClass}`}>
                {message}
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${okClass}`}
          >
            {okLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function useNoticeDialog(theme: 'dark' | 'light' = 'dark') {
  const [request, setRequest] = useState<NoticeRequest | null>(null);

  const showNotice = useCallback(
    (message: string, title?: string, okLabel?: string, variant?: NoticeVariant) => {
      setRequest({ message, title, okLabel, variant });
    },
    [],
  );

  const dialog = (
    <NoticeDialog
      open={!!request}
      title={request?.title}
      message={request?.message ?? ''}
      okLabel={request?.okLabel}
      variant={request?.variant}
      theme={theme}
      onClose={() => setRequest(null)}
    />
  );

  return { showNotice, dialog };
}
